import {
  criterionKey,
  DRILL_CRITERION_KINDS,
  type DrillCriterionKind,
  type DrillFrequency,
  type DrillFrequencyKind,
  type DrillTemplateContent,
  frequencyMonths,
} from '@felix/domain';
import type {
  DrillCategory,
  DrillLibrary,
  DrillTemplateDetail,
  DrillTemplateInput,
  DrillTemplateSummary,
} from './types.js';

/** Tatbikat kütüphanesi ekranının saf yardımcıları (ADR 0128 Faz 1). */

export const PAGE_SIZE = 25;

export type StatusFilter = 'all' | 'active' | 'inactive';

export interface TemplateFilter {
  /** Ana ya da alt kategori; boş: hepsi. */
  categoryId: string | null;
  query: string;
  status: StatusFilter;
}

/** Ana kategoriler sırayla, her birinin altında alt kategorileri. */
export function categoryTree(
  categories: readonly DrillCategory[],
): { category: DrillCategory; children: DrillCategory[] }[] {
  const order = (a: DrillCategory, b: DrillCategory) =>
    a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
  return categories
    .filter((c) => c.parentId === null)
    .sort(order)
    .map((category) => ({
      category,
      children: categories.filter((c) => c.parentId === category.id).sort(order),
    }));
}

const fold = (value: string) => value.toLocaleLowerCase('en').normalize('NFKD');

export function filterTemplates(
  library: DrillLibrary,
  filter: TemplateFilter,
): DrillTemplateSummary[] {
  const query = fold(filter.query.trim());
  const names = new Map(library.categories.map((c) => [c.id, c.name]));
  return library.templates.filter((t) => {
    if (
      filter.categoryId !== null &&
      t.categoryId !== filter.categoryId &&
      t.subcategoryId !== filter.categoryId
    ) {
      return false;
    }
    if (filter.status === 'active' && !t.active) return false;
    if (filter.status === 'inactive' && t.active) return false;
    if (query === '') return true;
    const text = [
      t.code,
      t.name,
      t.regulatoryReference ?? '',
      t.ruleCode ?? '',
      names.get(t.categoryId) ?? '',
      t.subcategoryId ? (names.get(t.subcategoryId) ?? '') : '',
    ].join(' ');
    return fold(text).includes(query);
  });
}

export function sortRows<T>(
  rows: readonly T[],
  value: (row: T) => string | number,
  dir: 'asc' | 'desc',
): T[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * sign;
    return String(x).localeCompare(String(y), undefined, { numeric: true }) * sign;
  });
}

export const pagesOf = (count: number, size = PAGE_SIZE) => Math.max(1, Math.ceil(count / size));

export function pageOf<T>(rows: readonly T[], page: number, size = PAGE_SIZE): T[] {
  return rows.slice((page - 1) * size, page * size);
}

/** Sıklık sıralama değeri: ay. */
export const frequencyOrder = (frequency: DrillFrequency) => frequencyMonths(frequency);

/* ---------- Düzenleme formu ---------- */

/** Formun değerleri: listeler çok satırlı metin (satır başına bir giriş). */
export interface TemplateForm {
  code: string;
  name: string;
  categoryId: string | null;
  subcategoryId: string | null;
  active: boolean;
  frequencyKind: DrillFrequencyKind;
  frequencyMonths: number | null;
  ruleCode: string | null;
  regulatoryReference: string;
  smsProcedure: string;
  vesselTypes: string;
  objective: string;
  scenario: string;
  equipment: string;
  roles: string;
  preparation: string;
  steps: string;
  safety: string;
  criteria: string;
  debriefing: string;
  correctiveActions: string;
  reportTemplate: string;
}

const lines = (items: readonly string[]) => items.join('\n');
const listOf = (text: string) =>
  text
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '');
const orNull = (text: string) => (text.trim() === '' ? null : text.trim());

export function formOfTemplate(
  detail: DrillTemplateDetail | null,
  categoryId: string | null = null,
): TemplateForm {
  const c = detail?.content;
  return {
    code: detail?.code ?? '',
    name: detail?.name ?? '',
    categoryId: detail?.categoryId ?? categoryId,
    subcategoryId: detail?.subcategoryId ?? null,
    active: detail?.active ?? true,
    frequencyKind: c?.frequency.kind ?? 'monthly',
    frequencyMonths: c?.frequency.months ?? null,
    ruleCode: c?.ruleCode ?? null,
    regulatoryReference: c?.regulatoryReference ?? '',
    smsProcedure: c?.smsProcedure ?? '',
    vesselTypes: lines(c?.vesselTypes ?? []),
    objective: c?.objective ?? '',
    scenario: c?.scenario ?? '',
    equipment: lines(c?.equipment ?? []),
    roles: lines((c?.roles ?? []).map((r) => (r.duty ? `${r.role}: ${r.duty}` : r.role))),
    preparation: lines(c?.preparation ?? []),
    steps: lines(c?.steps ?? []),
    safety: lines(c?.safety ?? []),
    criteria: lines((c?.criteria ?? []).map((k) => `${k.label} (${k.kind})`)),
    debriefing: lines(c?.debriefing ?? []),
    correctiveActions: lines(c?.correctiveActions ?? []),
    reportTemplate: c?.reportTemplate ?? '',
  };
}

const CRITERION = new RegExp(`^(.*?)\\s*\\((${DRILL_CRITERION_KINDS.join('|')})\\)$`, 'i');

/** "Muster Time (time)" → ölçüt; tür yazılmamışsa `rating`. */
export function criterionOf(line: string): {
  key: string;
  label: string;
  kind: DrillCriterionKind;
} {
  const match = CRITERION.exec(line.trim());
  const label = (match?.[1] ?? line).trim();
  const kind = (match?.[2]?.toLowerCase() ?? 'rating') as DrillCriterionKind;
  return { key: criterionKey(label), label, kind };
}

/** Formdan sunucuya giden şablon; alanları sunucudaki domain şeması denetler. */
export function templateOfForm(form: TemplateForm): DrillTemplateInput {
  const content: DrillTemplateContent = {
    vesselTypes: listOf(form.vesselTypes),
    regulatoryReference: orNull(form.regulatoryReference),
    smsProcedure: orNull(form.smsProcedure),
    objective: form.objective.trim(),
    scenario: form.scenario.trim(),
    equipment: listOf(form.equipment),
    roles: listOf(form.roles).map((line) => {
      const at = line.indexOf(':');
      return at > 0
        ? { role: line.slice(0, at).trim(), duty: orNull(line.slice(at + 1)) }
        : { role: line, duty: null };
    }),
    preparation: listOf(form.preparation),
    steps: listOf(form.steps),
    safety: listOf(form.safety),
    criteria: listOf(form.criteria).map(criterionOf),
    debriefing: listOf(form.debriefing),
    correctiveActions: listOf(form.correctiveActions),
    reportTemplate: orNull(form.reportTemplate),
    frequency: {
      kind: form.frequencyKind,
      months: form.frequencyKind === 'custom' ? form.frequencyMonths : null,
    },
    ruleCode: form.ruleCode,
  };
  return {
    code: form.code.trim().toUpperCase(),
    name: form.name.trim(),
    categoryId: form.categoryId ?? '',
    subcategoryId: form.subcategoryId,
    active: form.active,
    content,
  };
}

/** `INVALID_DRILL_TEMPLATE` ayrıntısı "content.steps:REQUIRED,name:TOO_LONG" → alan listesi. */
export function issuesOf(detail: string | undefined): { path: string; code: string }[] {
  if (!detail) return [];
  return detail
    .split(',')
    .map((part) => {
      const at = part.lastIndexOf(':');
      return at > 0 ? { path: part.slice(0, at), code: part.slice(at + 1) } : null;
    })
    .filter((issue): issue is { path: string; code: string } => issue !== null);
}
